import React, { useEffect, useState, useRef } from 'react';
import SessionManager from '../utils/sessionManager';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import useSpeech from '../hooks/useSpeech';
import { uploadInterviewAudio } from '../services/audioEvalApi';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const AssessmentInterviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const screenStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  // ─── Theme colors matching AITrainerSessionPage ───
  const colors = {
    primary: '#0d9488',
    primaryHover: '#0f766e',
    primaryLight: '#ccfbf1',
    primaryText: '#134e4a',
    bg: '#f6f6f8',
    bgDark: '#101622',
    card: '#ffffff',
    cardDark: '#1a2230',
    border: '#e7ebf3',
    borderDark: '#2a374f',
    text: '#0d121b',
    textSecondary: '#4c669a',
    textDark: '#f8f9fc',
    textSecondaryDark: '#9fb3d9',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1); // Start at -1 for intro
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answerPhase, setAnswerPhase] = useState('idle'); // idle, thinking, listening, uploading, saved
  const isEndingRef = useRef(false); // Guard against duplicate endInterview calls

  // Chirp 3 HD TTS + audio recording (Gemini 2.5 Flash evaluates on /complete)
  const {
    isSpeaking, isListening, isProcessing,
    speak, stopSpeaking,
    startListeningWindow: startSpeechListeningWindow,
    finishListeningWindow,
    cleanup: cleanupSpeech,
  } = useSpeech();
  const lastAudioBlobRef = useRef(null);
  const [interviewId, setInterviewId] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [warningCount, setWarningCount] = useState(0);
  const [showingIntro, setShowingIntro] = useState(true);
  const [faceDetectionStatus, setFaceDetectionStatus] = useState('checking'); // checking, ok, no_face, multiple_faces
  const [faceCount, setFaceCount] = useState(0);
  const [warningBanner, setWarningBanner] = useState({ show: false, message: '' });
  const [lastWarningTime, setLastWarningTime] = useState(0);
  const [gracePeriodActive, setGracePeriodActive] = useState(true);
  // Face detection tracking for continuous warnings
  const [noFaceStartTime, setNoFaceStartTime] = useState(null);
  const [cursorOutOfBounds, setCursorOutOfBounds] = useState(false);
  const [cursorWarningStartTime, setCursorWarningStartTime] = useState(null);
  const [showCursorBlink, setShowCursorBlink] = useState(false);
  const mouseTrackingRef = useRef(null);
  const proctoringIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const cursorBlinkTimeoutRef = useRef(null);
  const mixAudioContextRef = useRef(null);

  const applicationId = location.state?.applicationId;
  const candidateName = location.state?.candidateName || 'Candidate';
  const profilePhotoCapturedRef = useRef(false);

  useEffect(() => {
    if (!applicationId) {
      navigate('/jobs');
      return;
    }
    
    // Setup cursor tracking for proctoring
    setupCursorTracking();
    
    return () => {
      console.log('Cleaning up interview page...');
      stopProctoring();
      cleanupCursorTracking();
      cleanupSpeech();
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (e) {
          console.log('Audio context already closed');
        }
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Stop all streams on cleanup
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [applicationId, navigate]);

  // Effect to handle video stream when camera stream is available
  // Re-attach stream whenever the active video element changes (intro → main view)
  useEffect(() => {
    if (interviewStarted && cameraStreamRef.current && videoRef.current) {
      console.log('🎥 Setting camera stream to video element');
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current.play().then(() => {
        console.log('✓ Video playing');
      }).catch(err => {
        console.error('❌ Error playing video:', err);
      });
    }
  }, [interviewStarted, showingIntro]);

  // Text-to-Speech function using Chirp 3 HD
  const speakQuestion = (text) => {
    console.log('🔊 Speaking question:', text.substring(0, 50) + '...');
    speak(text, {
      onEnd: () => {
        console.log('✓ TTS ended, starting thinking time...');
        startThinkingThenListen();
      },
      onError: () => {
        console.error('TTS error');
        startThinkingThenListen();
      },
    });
  };

  // Thinking time then listening window
  const startThinkingThenListen = async () => {
    console.log('🤔 Starting 5-second thinking time...');
    setAnswerPhase('thinking');

    for (let i = 5; i > 0; i--) {
      await new Promise(r => setTimeout(r, 1000));
    }

    console.log('✓ Thinking time complete, starting listening window...');
    startListeningWindow();
  };

  // Record audio — on completion, upload to GCS (Gemini eval happens on /complete)
  const [listeningRemaining, setListeningRemaining] = useState(0);

  const startListeningWindow = () => {
    if (!cameraStreamRef.current) {
      console.error('❌ No camera stream for audio recording');
      return;
    }

    console.log('🎤 Starting audio recording (10-60 seconds)...');
    setAnswerPhase('listening');

    startSpeechListeningWindow(cameraStreamRef.current, {
      minSeconds: 10,
      maxSeconds: 60,
      onTick: ({ remaining }) => {
        setListeningRemaining(remaining);
      },
      onComplete: async (audioBlob) => {
        console.log('✓ Recording complete, uploading audio...');
        lastAudioBlobRef.current = audioBlob;
        setAnswerPhase('uploading');

        if (audioBlob && interviewId) {
          try {
            await uploadInterviewAudio(audioBlob, interviewId, currentQuestionIndex + 1);
            console.log('✓ Audio uploaded to GCS');
            // Also save the answer record (empty text — transcript comes from Gemini on /complete)
            await saveAnswer(currentQuestionIndex + 1, '', 60);
          } catch (err) {
            console.error('Audio upload error:', err);
            await saveAnswer(currentQuestionIndex + 1, '', 60);
          }
        }
        setAnswerPhase('saved');
      },
    });
  };

  // Stop recording
  const stopListening = () => {
    finishListeningWindow();
  };

  // Audio level monitoring
  const startAudioMonitoring = (stream) => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    analyserRef.current = audioContextRef.current.createAnalyser();
    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);
    analyserRef.current.fftSize = 256;
    
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const updateLevel = () => {
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / bufferLength;
      setAudioLevel(Math.min(100, (average / 128) * 100));
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  };

  // Issue warning with cooldown (reference implementation)
  const issueWarning = (message) => {
    const now = Date.now();
    const WARNING_COOLDOWN = 5000; // 5 seconds between warnings (reduced from 10)
    
    if (now - lastWarningTime < WARNING_COOLDOWN) {
      console.log('Warning cooldown active, skipping:', message);
      return;
    }
    
    console.log('🚨 ISSUING WARNING:', message);
    setLastWarningTime(now);
    setWarningCount(prev => prev + 1);
    
    // Show warning banner
    setWarningBanner({ show: true, message });
    
    // Hide banner after 5 seconds (reduced from 8)
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    warningTimeoutRef.current = setTimeout(() => {
      setWarningBanner({ show: false, message: '' });
    }, 5000);
    
    // Check if too many warnings
    if (warningCount + 1 >= 3) {
      setTimeout(() => {
        setWarningBanner({ 
          show: true, 
          message: '🚨 FINAL WARNING: You have received 3 warnings. Interview may be terminated.' 
        });
      }, 500);
    }
  };

  // Cursor tracking setup
  const setupCursorTracking = () => {
    if (!interviewStarted) return;
    
    // Only warn when cursor leaves the browser window entirely, not for internal movement
    const handleMouseMove = () => {};
    
    const handleMouseLeave = () => {
      if (!cursorOutOfBounds) {
        setCursorOutOfBounds(true);
        setCursorWarningStartTime(Date.now());
        // Trigger blink effect for 3 seconds
        setShowCursorBlink(true);
        if (cursorBlinkTimeoutRef.current) {
          clearTimeout(cursorBlinkTimeoutRef.current);
        }
        cursorBlinkTimeoutRef.current = setTimeout(() => {
          setShowCursorBlink(false);
        }, 3000);
        console.log('🖱️ Cursor left window');
      }
    };
    
    const handleMouseEnter = () => {
      if (cursorOutOfBounds) {
        setCursorOutOfBounds(false);
        setCursorWarningStartTime(null);
        setShowCursorBlink(false);
        if (cursorBlinkTimeoutRef.current) {
          clearTimeout(cursorBlinkTimeoutRef.current);
        }
        console.log('🖱️ Cursor entered window');
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    
    mouseTrackingRef.current = {
      handleMouseMove,
      handleMouseLeave,
      handleMouseEnter
    };
  };
  
  const cleanupCursorTracking = () => {
    if (mouseTrackingRef.current) {
      document.removeEventListener('mousemove', mouseTrackingRef.current.handleMouseMove);
      document.removeEventListener('mouseleave', mouseTrackingRef.current.handleMouseLeave);
      document.removeEventListener('mouseenter', mouseTrackingRef.current.handleMouseEnter);
      mouseTrackingRef.current = null;
    }
    if (cursorBlinkTimeoutRef.current) {
      clearTimeout(cursorBlinkTimeoutRef.current);
    }
  };

  // Check for continuous violations every second
  useEffect(() => {
    if (!interviewStarted || gracePeriodActive) return;
    
    const checkContinuousViolations = setInterval(() => {
      const now = Date.now();
      
      // Check for no face detection for 2+ seconds
      if (faceCount === 0) {
        if (!noFaceStartTime) {
          setNoFaceStartTime(now);
        } else if (now - noFaceStartTime >= 2000) {
          issueWarning('⚠️ No face detected for 2 seconds. Please show your face clearly.');
          setNoFaceStartTime(null); // Reset to avoid repeated warnings
        }
      } else {
        setNoFaceStartTime(null);
      }
      
      // Check for cursor out of bounds for 2+ seconds
      if (cursorOutOfBounds && cursorWarningStartTime) {
        if (now - cursorWarningStartTime >= 2000) {
          issueWarning('⚠️ Cursor moved outside interview area. Please keep focus on the interview.');
          setCursorWarningStartTime(null); // Reset to avoid repeated warnings
        }
      }
    }, 1000);
    
    return () => clearInterval(checkContinuousViolations);
  }, [interviewStarted, gracePeriodActive, faceCount, cursorOutOfBounds, cursorWarningStartTime, noFaceStartTime, warningCount, lastWarningTime]);

  // Proctoring - Face detection using MediaPipe + YOLO (reference implementation)
  const analyzeFrame = async () => {
    if (!videoRef.current || !interviewStarted || !interviewId) {
      console.log('⏭️ Skipping frame analysis:', {
        hasVideo: !!videoRef.current,
        interviewStarted,
        interviewId
      });
      return;
    }

    try {
      console.log('📸 Capturing frame for analysis...');
      
      // Capture frame from video
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      if (canvas.width === 0 || canvas.height === 0) {
        console.log('⚠️ Video dimensions not ready');
        return;
      }
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      // Convert to base64
      const frameData = canvas.toDataURL('image/jpeg', 0.8);
      console.log('✓ Frame captured, sending to backend...');
      
      // Send to backend for analysis
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/analyze-frame', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_id: interviewId,
          question_number: currentQuestionIndex + 1,
          frame_data: frameData
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✓ Frame analysis response:', data);
        const analysis = data.analysis;
        
        if (!analysis) {
          console.error('No analysis data in response');
          return;
        }
        
        setFaceCount(analysis.face_count || 0);
        console.log(`Face count: ${analysis.face_count}, Warnings: ${analysis.warnings?.join(', ') || 'none'}`);
        
        // Issue warnings for violations - IMMEDIATE for face detection issues
        if (analysis.warnings && analysis.warnings.length > 0) {
          const warning = analysis.warnings[0];
          let message = '';
          let shouldWarn = true;
          let isImmediate = false; // Some warnings should be immediate
          
          // Handle different warning types
          switch(warning) {
            case 'no_face':
              message = '⚠️ No face detected. Please show your face clearly.';
              setFaceDetectionStatus('no_face');
              isImmediate = true; // Face detection is immediate
              break;
            case 'multiple_faces':
              // IMMEDIATE WARNING for multiple faces
              message = '⚠️ Multiple faces detected. Only you should be visible.';
              setFaceDetectionStatus('multiple_faces');
              isImmediate = true; // Multiple faces is immediate
              break;
            case 'flat_surface':
              message = '⚠️ Face covered or object detected. Please remove it.';
              setFaceDetectionStatus('no_face');
              isImmediate = true; // Face covered is immediate
              break;
            case 'too_dark':
              message = '⚠️ Video too dark. Please ensure good lighting.';
              break;
            case 'too_bright':
              message = '⚠️ Video too bright. Please adjust lighting.';
              break;
            default:
              message = '⚠️ Face detection issue. Please ensure you are visible.';
              isImmediate = true;
          }
          
          // Issue warning if grace period is over OR if it's an immediate warning
          if ((isImmediate || !gracePeriodActive) && shouldWarn && message) {
            issueWarning(message);
            // Log to backend
            await logProctoringEvent(warning, warning === 'multiple_faces' ? 'critical' : 'warning', {
              message: message,
              face_count: analysis.face_count
            });
          }
        } else if (analysis.face_count === 1) {
          // Face detected, all good
          setFaceDetectionStatus('ok');
        }
        
        // Immediate warning for device detection (outside grace period)
        if (!gracePeriodActive && analysis.device_detected) {
          issueWarning('⚠️ Phone or device detected. Please remove it from the camera view.');
          await logProctoringEvent('device_detected', 'critical', {
            message: 'Device detected in frame',
            device_count: analysis.objects?.length || 1
          });
        }
      } else {
        console.error('Frame analysis failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (err) {
      console.error('Error analyzing frame:', err);
      console.error('Error stack:', err.stack);
    }
  };

  const logProctoringEvent = async (eventType, severity, details) => {
    try {
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/log-proctoring-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_id: interviewId,
          question_number: currentQuestionIndex + 1,
          event_type: eventType,
          severity: severity,
          details: details
        })
      });

      if (response.ok) {
        const data = await response.json();
        setWarningCount(data.total_warnings);
        
        // Add warning to list
        setWarnings(prev => [...prev, {
          type: eventType,
          message: details.message,
          timestamp: new Date().toISOString()
        }].slice(-5)); // Keep last 5 warnings
      }
    } catch (err) {
      console.error('Error logging proctoring event:', err);
    }
  };

  // Start proctoring when interview starts (with shorter grace period)
  const startProctoring = () => {
    console.log('🔍 Starting proctoring with 3-second grace period...');
    
    // Grace period: 3 seconds before issuing warnings (reduced from 10)
    setGracePeriodActive(true);
    setTimeout(() => {
      setGracePeriodActive(false);
      console.log('✓ Grace period ended, warnings now active');
    }, 3000); // Reduced from 10000 to 3000
    
    // Analyze frame every 2 seconds (more frequent)
    proctoringIntervalRef.current = setInterval(() => {
      analyzeFrame();
    }, 2000); // Reduced from 3000 to 2000
  };

  const stopProctoring = () => {
    if (proctoringIntervalRef.current) {
      clearInterval(proctoringIntervalRef.current);
      proctoringIntervalRef.current = null;
      console.log('🔍 Proctoring stopped');
    }
  };

  const generateQuestions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/interview/generate-questions`, {
        method: 'POST',
        headers: { ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: applicationId,
          num_questions: 5
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate questions');
      }

      const data = await response.json();
      console.log('Questions generated:', data);
      setQuestions(data.questions);
      setInterviewId(data.interview_id);
      
      // Check if we can resume from a previous session
      if (data.can_resume && data.current_question_index > 0) {
        console.log(`📍 Resuming interview from question ${data.current_question_index + 1}`);
        console.log(`✓ Already answered questions: ${data.answered_questions?.join(', ')}`);
        
        // Show resume dialog
        const shouldResume = window.confirm(
          `You have an incomplete interview. You've answered ${data.answered_questions?.length || 0} out of ${data.questions.length} questions.\n\n` +
          `Would you like to resume from question ${data.current_question_index + 1}?\n\n` +
          `Click OK to resume, or Cancel to start over.`
        );
        
        if (shouldResume) {
          // Set the current question index to resume from
          setCurrentQuestionIndex(data.current_question_index - 1); // Will be incremented when starting
          console.log(`✓ Will resume from question ${data.current_question_index}`);
        } else {
          console.log('User chose to start over');
          // Reset progress on backend
          await updateProgress(0, data.questions.length);
        }
      }
      
      setLoading(false);
      return data.questions;
    } catch (err) {
      console.error('Error generating questions:', err);
      setError(err.message || 'Failed to generate questions. Please try again.');
      setLoading(false);
      return null;
    }
  };

  // Mix multiple audio tracks into a single track using Web Audio API
  const mixAudioTracks = (streams) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const destination = ctx.createMediaStreamDestination();

    streams.forEach(stream => {
      stream.getAudioTracks().forEach(track => {
        const source = ctx.createMediaStreamSource(new MediaStream([track]));
        source.connect(destination);
      });
    });

    // Store context for cleanup
    mixAudioContextRef.current = ctx;
    return destination.stream.getAudioTracks();
  };

  const startRecording = (combinedStream) => {
    try {
      const options = { mimeType: 'video/webm;codecs=vp9' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm';
        }
      }

      mediaRecorderRef.current = new MediaRecorder(combinedStream, options);
      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      // Capture question index at recording start to avoid stale closure issues
      const questionIndex = currentQuestionIndex;

      mediaRecorderRef.current.onstop = async () => {
        const chunks = [...recordedChunksRef.current];
        recordedChunksRef.current = [];

        if (chunks.length === 0) {
          console.warn('⚠️ No recorded chunks to upload');
          return;
        }

        const blob = new Blob(chunks, { type: options.mimeType });
        console.log(`📹 Recording blob: ${(blob.size / 1024 / 1024).toFixed(1)}MB, type: ${options.mimeType}`);

        // Upload to backend
        const formData = new FormData();
        formData.append('video', blob, `interview-${interviewId}-q${questionIndex + 1}.webm`);
        formData.append('interview_id', interviewId);
        formData.append('question_number', questionIndex + 1);

        try {
          const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/upload-video', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            const data = await response.json();
            console.log(`✓ Video uploaded for Q${questionIndex + 1}:`, data.video_path);
          } else {
            console.error(`Failed to upload video for Q${questionIndex + 1}`);
          }
        } catch (err) {
          console.error('Error uploading video:', err);
        }
      };

      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const startInterview = async () => {
    // Generate questions first
    const questionsGenerated = await generateQuestions();
    
    if (!questionsGenerated || questionsGenerated.length === 0) {
      setError('Failed to generate interview questions. Please check your connection and try again.');
      return;
    }
    
    // Request screen share and camera FIRST
    try {
      // Start camera first
      const cameraStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      cameraStreamRef.current = cameraStream;
      console.log('✓ Camera stream obtained');
      
      // Request screen share
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen' },
        audio: true
      });
      
      screenStreamRef.current = screenStream;
      console.log('✓ Screen stream obtained');
      
      // NOW set interview started so video element renders
      setInterviewStarted(true);
      setShowingIntro(true);
      setCurrentQuestionIndex(-1);
      
      // Wait for DOM to update, then set video stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = cameraStream;
          console.log('✓ Camera stream set to video element');
          
          // Ensure video plays
          videoRef.current.play().then(() => {
            console.log('✓ Video playing successfully');
            // Capture profile photo once after 3 seconds (when face is settled)
            if (!profilePhotoCapturedRef.current) {
              setTimeout(() => {
                try {
                  if (!videoRef.current || profilePhotoCapturedRef.current) return;
                  const canvas = document.createElement('canvas');
                  canvas.width = videoRef.current.videoWidth;
                  canvas.height = videoRef.current.videoHeight;
                  if (canvas.width === 0) return;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(videoRef.current, 0, 0);
                  const frameData = canvas.toDataURL('image/jpeg', 0.85);
                  // Get candidate_id from localStorage
                  const currentRole = localStorage.getItem('dutlaunch_current_session');
                  const candidateId = currentRole ? localStorage.getItem(`dutlaunch_${currentRole}_user_id`) : null;
                  if (candidateId) {
                    profilePhotoCapturedRef.current = true;
                    fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/capture-profile-photo', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ candidate_id: candidateId, frame_data: frameData })
                    }).then(() => console.log('✓ Profile photo captured')).catch(() => {});
                  }
                } catch (e) { /* silent */ }
              }, 3000);
            }
          }).catch(err => {
            console.error('Error playing video:', err);
          });
        } else {
          console.error('❌ Video ref is still null after timeout');
        }
      }, 200);
      
      // Combine screen video with mixed audio (single track) for recording
      const mixedAudioTracks = mixAudioTracks([screenStream, cameraStream]);
      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...mixedAudioTracks
      ]);
      
      // Start audio monitoring
      startAudioMonitoring(cameraStream);
      
      // Start recording
      startRecording(combinedStream);
      
      // Proctoring will start when first question begins (in startFirstQuestion)
      
      // Speak welcome message after a delay
      setTimeout(() => {
        const welcomeMessage = `Hello ${candidateName}, welcome to your AI interview. This interview will consist of ${questionsGenerated.length} questions. Please answer each question clearly and confidently. When you're ready, click the button to begin with the first question.`;
        speak(welcomeMessage);
      }, 1500);
      
    } catch (err) {
      setError('Camera and screen sharing are required to start the interview');
      console.error('Error starting interview:', err);
    }
  };

  const startFirstQuestion = () => {
    console.log('Starting first question');
    setShowingIntro(false);
    setCurrentQuestionIndex(0);
    setAnswerPhase('idle');
    
    // Start proctoring now that interview has actually started
    if (interviewId) {
      console.log('🔍 Starting proctoring for interview:', interviewId);
      startProctoring();
      // Setup cursor tracking after proctoring starts
      setTimeout(() => {
        setupCursorTracking();
      }, 1000);
    }
    
    // Speak first question
    setTimeout(() => {
      if (questions.length > 0) {
        speakQuestion(questions[0].question_text);
      }
    }, 500);
  };

  const saveAnswer = async (questionNumber, answerText, duration) => {
    try {
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/save-answer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_id: interviewId,
          question_number: questionNumber,
          answer_text: answerText,
          duration_seconds: duration
        })
      });
      
      if (response.ok) {
        console.log(`Answer ${questionNumber} saved successfully`);
        
        // Update progress after saving answer
        await updateProgress(questionNumber, questions.length);
      }
    } catch (err) {
      console.error('Error saving answer:', err);
    }
  };
  
  const updateProgress = async (currentIndex, totalQuestions) => {
    try {
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/update-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_id: interviewId,
          current_question_index: currentIndex,
          total_questions: totalQuestions
        })
      });
      
      if (response.ok) {
        console.log(`✓ Progress updated: ${currentIndex}/${totalQuestions}`);
      }
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  const skipQuestion = async () => {
    // Stop current recording
    stopRecording();
    stopListening();
    stopSpeaking();

    // Upload any recorded audio, or save empty answer for skipped question
    if (lastAudioBlobRef.current && interviewId) {
      try {
        await uploadInterviewAudio(lastAudioBlobRef.current, interviewId, currentQuestionIndex + 1);
      } catch (err) {
        console.error('Audio upload on skip:', err);
      }
    }
    await saveAnswer(currentQuestionIndex + 1, '', 120);
    lastAudioBlobRef.current = null;

    if (currentQuestionIndex < questions.length - 1) {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setAnswerPhase('idle');

      // Wait for speech recognition to fully stop before restarting
      setTimeout(() => {
        // Restart recording for next question
        if (screenStreamRef.current && cameraStreamRef.current) {
          const mixedAudioTracks = mixAudioTracks([screenStreamRef.current, cameraStreamRef.current]);
          const combinedStream = new MediaStream([
            ...screenStreamRef.current.getVideoTracks(),
            ...mixedAudioTracks
          ]);
          startRecording(combinedStream);
        }

        // Speak next question after a longer delay to ensure recognition is fully stopped
        setTimeout(() => {
          console.log(`📢 Speaking question ${currentQuestionIndex + 2}`);
          speakQuestion(questions[currentQuestionIndex + 1].question_text);
        }, 500);
      }, 300);
    } else {
      // Last question - end the interview
      await endInterview();
    }
  };

  // Cleanup function to stop all streams and processes immediately
  const cleanupInterview = () => {
    console.log('🛑 Cleaning up interview - stopping all streams...');
    
    // Stop proctoring
    stopProctoring();
    cleanupCursorTracking();
    
    // Stop recording immediately
    stopRecording();
    stopListening();
    stopSpeaking();
    
    // Stop all camera and screen streams immediately
    if (screenStreamRef.current) {
      console.log('🛑 Stopping screen stream...');
      screenStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('✓ Screen track stopped:', track.kind);
      });
      screenStreamRef.current = null;
    }
    
    if (cameraStreamRef.current) {
      console.log('🛑 Stopping camera stream...');
      cameraStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('✓ Camera track stopped:', track.kind);
      });
      cameraStreamRef.current = null;
    }
    
    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      console.log('✓ Video element cleared');
    }
    
    // Stop audio monitoring and mixing
    if (mixAudioContextRef.current && mixAudioContextRef.current.state !== 'closed') {
      mixAudioContextRef.current.close();
      mixAudioContextRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    setIsRecording(false);
    setInterviewStarted(false);
    
    console.log('✅ Interview cleanup complete - camera should be off');
  };

  const endInterview = async () => {
    // Prevent duplicate calls — this was causing 30+ simultaneous /complete requests
    if (isEndingRef.current) {
      console.log('⏭️ endInterview already in progress, skipping duplicate call');
      return;
    }
    isEndingRef.current = true;

    // Cleanup and navigate IMMEDIATELY — don't make candidate wait for evaluation
    cleanupInterview();
    navigate('/assessment/complete', {
      state: {
        applicationId: applicationId,
        interviewId: interviewId
      }
    });

    // Fire-and-forget: upload final audio + mark complete in background
    // Backend handles evaluation asynchronously — candidate doesn't need to wait
    const savedInterviewId = interviewId;
    const savedQuestionIndex = currentQuestionIndex;
    const savedAudioBlob = lastAudioBlobRef.current;
    lastAudioBlobRef.current = null;

    try {
      if (savedAudioBlob && savedInterviewId) {
        await uploadInterviewAudio(savedAudioBlob, savedInterviewId, savedQuestionIndex + 1);
        await saveAnswer(savedQuestionIndex + 1, '', 120);
      }
    } catch (err) {
      console.error('Final audio upload error:', err);
    }

    try {
      await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/interview/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_id: savedInterviewId,
          status: 'completed'
        })
      });
    } catch (err) {
      console.error('Error completing interview:', err);
    }
  };

  // Cancel interview function
  const cancelInterview = () => {
    console.log('🚫 Interview cancelled by user');
    
    // Cleanup everything immediately
    cleanupInterview();
    
    // Wait a moment for cleanup to complete before navigation
    setTimeout(() => {
      // Navigate back
      navigate(-1);
    }, 500); // 500ms delay to ensure cleanup completes
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentQuestionIndex >= questions.length - 1;

  // ─── Phase badge helper ───
  const getPhaseBadge = () => {
    switch (answerPhase) {
      case 'thinking': return { color: 'bg-amber-500', text: 'Thinking...', icon: 'psychology' };
      case 'listening': return { color: 'bg-green-500', text: `Recording (${listeningRemaining}s)`, icon: 'mic' };
      case 'uploading': return { color: 'bg-blue-500', text: 'Saving...', icon: 'cloud_upload' };
      case 'saved': return { color: 'bg-teal-500', text: 'Saved', icon: 'check_circle' };
      default: return isSpeaking ? { color: 'bg-blue-500', text: 'AI Speaking...', icon: 'volume_up' } : { color: 'bg-gray-400', text: 'Waiting', icon: 'hourglass_empty' };
    }
  };

  // ═════════════════════ LOADING ═════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: colors.bg }}>
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-teal-500 animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon name="assignment" size={28} style={{ color: colors.primary }} />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Preparing Your Interview</h2>
          <p className="text-gray-500 text-sm">Generating questions...</p>
        </div>
      </div>
    );
  }

  // ═════════════════════ PRE-START / WELCOME ═════════════════════
  if (!interviewStarted) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: colors.bg }}>
        {/* Blinking border frame when cursor moves out */}
        {showCursorBlink && (
          <div className="fixed inset-0 z-50 pointer-events-none border-8 border-red-500 animate-pulse" style={{ animation: 'blink 0.5s ease-in-out 6' }} />
        )}
        <div className="max-w-lg text-center">
          <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: colors.primaryLight }}>
            <Icon name="videocam" size={48} style={{ color: colors.primary }} />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Ready to Start Your AI Interview?</h2>
          <p className="text-gray-500 text-base mb-8 leading-relaxed">
            This interview will consist of <span className="font-bold text-gray-700">{questions.length > 0 ? questions.length : 10} questions</span>.
            Please ensure you're in a quiet environment and ready to share your screen.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={startInterview}
            disabled={loading}
            className="w-full py-4 px-8 rounded-xl text-white font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 mb-3"
            style={{ background: loading ? '#94a3b8' : colors.primary }}
          >
            <Icon name="play_arrow" size={24} />
            {loading ? 'Generating Questions...' : 'Start Interview'}
          </button>
          <button
            onClick={cancelInterview}
            className="w-full py-3 px-8 rounded-xl text-sm font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-all duration-200"
          >
            Cancel
          </button>
          <p className="text-xs text-gray-400 mt-4">Camera, microphone, and screen sharing are required</p>
        </div>
      </div>
    );
  }

  // ═════════════════════ INTRO (interview started, showing welcome) ═════════════════════
  if (showingIntro) {
    return (
      <div className="flex h-screen w-full overflow-hidden" style={{ background: colors.bg }}>
        {/* Warning Banner */}
        {warningBanner.show && (
          <div className="absolute top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-3 text-center font-semibold text-sm">
            <Icon name="warning" size={16} className="inline mr-2" />{warningBanner.message}
          </div>
        )}
        {/* Blinking border frame */}
        {showCursorBlink && (
          <div className="fixed inset-0 z-50 pointer-events-none border-8 border-red-500 animate-pulse" style={{ animation: 'blink 0.5s ease-in-out 6' }} />
        )}

        {/* ─── Left: Main Content ─── */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <div className="h-14 px-6 flex items-center justify-between bg-white border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.primary }}>
                <Icon name="assignment" size={18} className="text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900">AI Interview Assessment</h1>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: colors.primaryLight, color: colors.primaryText }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.success }}></div>
              AI Ready
            </div>
          </div>

          {/* Welcome Content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-lg text-center">
              <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: colors.primaryLight }}>
                <Icon name="school" size={48} style={{ color: colors.primary }} />
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Ready to Begin?</h2>
              <p className="text-gray-500 text-base mb-8 leading-relaxed">
                Your interview consists of <span className="font-bold text-gray-700">{questions.length} questions</span>.
                Please answer each question clearly and confidently. Your responses will be <span className="font-bold text-gray-700">recorded and transcribed</span> automatically.
              </p>

              {/* Speaking indicator */}
              {isSpeaking && (
                <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-xl bg-blue-50 border border-blue-100" style={{ minHeight: '48px' }}>
                  <div className="flex items-center gap-1" style={{ height: '24px' }}>
                    {[12, 18, 24, 16, 20].map((h, i) => (
                      <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${h}px`, animationDelay: `${i * 100}ms` }} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-blue-600 ml-2">AI is speaking introduction...</span>
                </div>
              )}

              {/* Start Button */}
              <button
                onClick={startFirstQuestion}
                disabled={isSpeaking}
                className="w-full py-4 px-8 rounded-xl text-white font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                style={{ background: isSpeaking ? '#94a3b8' : colors.primary }}
              >
                <Icon name="play_arrow" size={24} />
                {isSpeaking ? 'Listening to Introduction...' : 'Start First Question'}
              </button>
              <p className="text-xs text-gray-400 mt-4">Camera, microphone, and screen are being recorded</p>
            </div>
          </div>
        </div>

        {/* ─── Right: Camera Panel ─── */}
        <div className="w-[350px] bg-white border-l border-gray-200 flex flex-col">
          {/* Camera Feed */}
          <div className="relative bg-gray-900 overflow-hidden" style={{ height: '280px' }}>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            {/* Face detection badge */}
            <div className="absolute top-3 right-3 z-10">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
                faceDetectionStatus === 'ok' ? 'bg-green-500/90 text-white' : 'bg-amber-500/90 text-white'
              }`}>
                <Icon name={faceDetectionStatus === 'ok' ? 'face' : 'face_retouching_off'} size={14} />
                {faceDetectionStatus === 'ok' ? 'Detected' : 'Checking'}
              </div>
            </div>
            {/* Recording badge */}
            {isRecording && (
              <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/90 text-white text-xs font-semibold backdrop-blur-sm">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                REC
              </div>
            )}
          </div>

          {/* Monitoring */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Monitoring</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: 'face', label: 'Face', status: faceDetectionStatus === 'ok' ? 'OK' : 'Checking', ok: faceDetectionStatus === 'ok' },
                { icon: 'visibility', label: 'Eyes', status: 'Focused', ok: true },
                { icon: 'mic', label: 'Mic', status: cameraStreamRef.current ? 'Ready' : 'Off', ok: !!cameraStreamRef.current },
              ].map(item => (
                <div key={item.label} className="text-center p-2.5 bg-gray-50 rounded-lg">
                  <Icon name={item.icon} size={20} className={`mx-auto mb-1 ${item.ok ? 'text-green-500' : 'text-amber-500'}`} />
                  <div className="text-[10px] font-semibold text-gray-500">{item.label}</div>
                  <div className={`text-[10px] font-bold ${item.ok ? 'text-green-600' : 'text-amber-600'}`}>{item.status}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Session Info */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Session Info</h3>
            <div className="space-y-2.5">
              {[
                { label: 'Candidate', value: candidateName },
                { label: 'Questions', value: `${questions.length} total` },
                { label: 'Proctoring', value: 'Active' },
                { label: 'Screen Share', value: screenStreamRef.current ? 'Active' : 'Pending' },
              ].map(item => (
                <div key={item.label} className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className="text-xs font-bold text-gray-700">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="p-4 mt-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.success }}></div>
                <span className="text-xs font-semibold text-gray-600">Ready</span>
              </div>
              <span className="text-xs font-bold text-gray-400">0/{questions.length}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═════════════════════ MAIN INTERVIEW INTERFACE ═════════════════════
  const phaseBadge = getPhaseBadge();

  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: colors.bg }}>
      {/* Warning Banner */}
      {warningBanner.show && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-3 text-center font-semibold text-sm animate-pulse">
          <Icon name="warning" size={16} className="inline mr-2" />{warningBanner.message}
        </div>
      )}
      {/* Blinking border frame when cursor moves out */}
      {showCursorBlink && (
        <div className="fixed inset-0 z-50 pointer-events-none border-8 border-red-500 animate-pulse" style={{ animation: 'blink 0.5s ease-in-out 6' }} />
      )}
      {/* Invisible cursor tracking overlay */}
      {interviewStarted && !showingIntro && (
        <div className="fixed inset-0 z-10 pointer-events-none" style={{
          background: `linear-gradient(to right, rgba(255,0,0,0.1) 0%, transparent 2%, transparent 98%, rgba(255,0,0,0.1) 100%), linear-gradient(to bottom, rgba(255,0,0,0.1) 0%, transparent 80px, transparent calc(100% - 100px), rgba(255,0,0,0.1) 100%)`
        }} />
      )}

      {/* ─── Left: Question & Controls ─── */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-14 px-6 flex items-center justify-between bg-white border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.primary }}>
              <Icon name="assignment" size={18} className="text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">AI Interview Assessment</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Phase Badge */}
            <div className={`flex items-center gap-2 text-xs font-semibold text-white px-3 py-1.5 rounded-full ${phaseBadge.color}`}>
              <Icon name={phaseBadge.icon} size={14} />
              {phaseBadge.text}
            </div>
            {/* Warning Count */}
            {warningCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-red-100 text-red-600">
                <Icon name="warning" size={14} />
                {warningCount}{/* /3 */}
              </div>
            )}
            {/* Progress */}
            <span className="text-xs font-bold text-gray-500">
              Q{currentQuestionIndex + 1}/{questions.length}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: colors.primary }}></div>
        </div>

        {/* Question Area */}
        <div className="flex-1 flex flex-col p-6 overflow-auto">
          {/* Question Card */}
          {currentQuestion && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: colors.primaryLight }}>
                  <span className="text-sm font-black" style={{ color: colors.primary }}>Q{currentQuestionIndex + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: colors.primaryLight, color: colors.primaryText }}>
                      {currentQuestion.category || 'interview'}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 leading-snug">
                    {currentQuestion.question_text}
                  </h2>
                </div>
              </div>
            </div>
          )}

          {/* Status Area */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 min-h-[200px] flex flex-col">
            {/* AI Speaking */}
            {isSpeaking && answerPhase !== 'saved' && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4" style={{ minHeight: '52px' }}>
                <div className="flex items-center gap-1" style={{ height: '26px' }}>
                  {[14, 20, 26, 18, 22].map((h, i) => (
                    <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${h}px`, animationDelay: `${i * 120}ms` }} />
                  ))}
                </div>
                <span className="text-sm font-semibold text-blue-600">AI is reading the question...</span>
              </div>
            )}

            {/* Thinking */}
            {answerPhase === 'thinking' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: '#fef3c7' }}>
                  <Icon name="psychology" size={32} className="text-amber-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">Take a moment to think...</h3>
                <p className="text-sm text-gray-500">Organize your thoughts before answering</p>
              </div>
            )}

            {/* Listening — Recording */}
            {answerPhase === 'listening' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="relative mb-5">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center bg-green-100 animate-pulse">
                    <Icon name="mic" size={36} className="text-green-600" />
                  </div>
                  <div className="absolute -inset-2 rounded-full border-2 border-green-400 animate-ping opacity-30"></div>
                </div>
                <div className="flex items-center gap-1.5 mb-3">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 bg-green-500 rounded-full"
                      style={{
                        height: `${8 + Math.sin(Date.now() / 200 + i) * 16}px`,
                        animation: 'pulse 0.6s ease-in-out infinite',
                        animationDelay: `${i * 70}ms`,
                      }}
                    />
                  ))}
                </div>
                <h3 className="text-lg font-bold text-green-700 mb-1">Listening to your answer...</h3>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Icon name="timer" size={16} className="text-green-600" />
                    <span className="font-bold text-green-600">{listeningRemaining}s remaining</span>
                  </div>
                </div>

                {/* Submit Early Button */}
                <button
                  onClick={stopListening}
                  className="mt-5 px-6 py-2.5 rounded-xl text-white font-semibold text-sm transition-all duration-200 hover:shadow-md"
                  style={{ background: colors.primary }}
                >
                  <Icon name="check" size={16} className="inline mr-1.5" />
                  Submit Answer
                </button>
              </div>
            )}

            {/* Uploading */}
            {answerPhase === 'uploading' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="relative w-16 h-16 mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name="cloud_upload" size={24} className="text-blue-500" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-blue-700 mb-1">Saving your answer...</h3>
                <p className="text-sm text-gray-500">Uploading audio recording</p>
              </div>
            )}

            {/* Saved */}
            {answerPhase === 'saved' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: '#d1fae5' }}>
                  <Icon name="check_circle" size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-700 mb-1">Answer Saved!</h3>
                <p className="text-sm text-gray-500">Click Next Question to continue</p>
              </div>
            )}

            {/* Idle — waiting */}
            {answerPhase === 'idle' && !isSpeaking && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Icon name="headphones" size={40} className="text-gray-300 mb-3" />
                <p className="text-sm text-gray-400">Waiting for AI to speak the question...</p>
              </div>
            )}
          </div>

          {/* Warning Display */}
          {warningCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mt-4">
              <div className="flex items-center gap-2">
                <Icon name="warning" size={20} className="text-yellow-600" />
                <span className="text-yellow-800 font-medium text-sm">Warnings: {warningCount}{/* /3 */}</span>
              </div>
              {warnings.length > 0 && (
                <ul className="mt-2 text-xs text-yellow-700 space-y-1">
                  {warnings.slice(-2).map((warning, idx) => (
                    <li key={idx}>- {warning.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ─── Bottom Action Bar ─── */}
        <div className="h-16 px-6 flex items-center justify-center gap-3 bg-white border-t border-gray-200">
          <button
            onClick={skipQuestion}
            disabled={isSpeaking || answerPhase === 'uploading'}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#f1f5f9', color: '#64748b' }}
          >
            <Icon name="skip_next" size={18} />
            Skip
          </button>

          {answerPhase === 'listening' && (
            <button
              onClick={stopListening}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
              style={{ background: colors.primary }}
            >
              <Icon name="check_circle" size={18} />
              Submit Answer
            </button>
          )}

          {answerPhase === 'saved' && (
            <button
              onClick={isLastQuestion ? endInterview : skipQuestion}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
              style={{ background: colors.primary }}
            >
              <Icon name="arrow_forward" size={18} />
              {isLastQuestion ? 'Complete Interview' : 'Next Question'}
            </button>
          )}

          <button
            onClick={endInterview}
            disabled={answerPhase === 'uploading'}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
          >
            <Icon name="stop_circle" size={18} />
            End Interview
          </button>
        </div>
      </div>

      {/* ─── Right: Camera Panel ─── */}
      <div className="w-[350px] bg-white border-l border-gray-200 flex flex-col">
        {/* Camera Feed */}
        <div className="relative bg-gray-900 overflow-hidden" style={{ height: '280px' }}>
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
          {/* Recording badge */}
          {isRecording && (
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/90 text-white text-xs font-semibold backdrop-blur-sm">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              REC
            </div>
          )}
          {/* Face badge */}
          <div className="absolute top-3 right-3 z-10">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
              faceDetectionStatus === 'ok' ? 'bg-green-500/90 text-white' : faceDetectionStatus === 'no_face' ? 'bg-red-500/90 text-white' : 'bg-amber-500/90 text-white'
            }`}>
              <Icon name={faceDetectionStatus === 'ok' ? 'face' : 'face_retouching_off'} size={14} />
              {faceDetectionStatus === 'ok' ? 'Detected' : faceDetectionStatus === 'no_face' ? 'No Face' : faceDetectionStatus === 'multiple_faces' ? `${faceCount} Faces` : 'Checking'}
            </div>
          </div>
          {/* Warning count badge */}
          {warningCount > 0 && (
            <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/90 text-white text-xs font-semibold backdrop-blur-sm">
              <Icon name="warning" size={14} />
              {warningCount}{/* /3 */}
            </div>
          )}
          {/* Proctoring notice */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/40 backdrop-blur-sm">
            <p className="text-[10px] text-white/70 text-center">AI Proctoring Active</p>
          </div>
        </div>

        {/* Monitoring */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Monitoring</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: 'face', label: 'Face', status: faceDetectionStatus === 'ok' ? 'OK' : faceDetectionStatus === 'no_face' ? 'None' : 'Checking', ok: faceDetectionStatus === 'ok' },
              { icon: 'visibility', label: 'Eyes', status: 'Focused', ok: true },
              { icon: isListening ? 'mic' : 'mic_off', label: 'Mic', status: isListening ? 'Active' : 'Standby', ok: isListening },
            ].map(item => (
              <div key={item.label} className="text-center p-2.5 bg-gray-50 rounded-lg">
                <Icon name={item.icon} size={20} className={`mx-auto mb-1 ${item.ok ? 'text-green-500' : 'text-amber-500'}`} />
                <div className="text-[10px] font-semibold text-gray-500">{item.label}</div>
                <div className={`text-[10px] font-bold ${item.ok ? 'text-green-600' : 'text-amber-600'}`}>{item.status}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Session Info */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Session</h3>
          <div className="space-y-2.5">
            {[
              { label: 'Candidate', value: candidateName },
              { label: 'Progress', value: `${currentQuestionIndex + 1} of ${questions.length}` },
              { label: 'Category', value: currentQuestion?.category || '-' },
              { label: 'Warnings', value: `${warningCount}` },
              { label: 'Cursor', value: cursorOutOfBounds ? 'Out of Bounds' : 'In Bounds' },
            ].map(item => (
              <div key={item.label} className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{item.label}</span>
                <span className={`text-xs font-bold ${item.label === 'Warnings' && warningCount > 0 ? 'text-red-600' : item.label === 'Cursor' && cursorOutOfBounds ? 'text-red-600' : 'text-gray-700'}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress */}
        <div className="p-4 mt-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Progress</span>
            <span className="text-xs font-bold" style={{ color: colors.primary }}>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: colors.primary }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentInterviewPage;
